import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { bankOptions } from '../../utils/Enums';
import SupplierFormSteps, { SUPPLIER_DETAILS_FIELDS } from './SupplierFormSteps';

const toNumberOrZero = (value) => {
  const number = Number.parseInt(value, 10);
  return Number.isNaN(number) ? 0 : number;
};

const EditSupplierModal = ({ open, onClose, onSave, supplier }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const steps = [t('supplier_details'), t('bank_details'), t('delivery_details')];

  useEffect(() => {
    if (supplier && open) {
      setFormData({
        name: supplier.name || '',
        business_name: supplier.businessName || '',
        rut: supplier.rut || '',
        pickup_address: supplier.pickupAddress || '',
        business_phone: supplier.businessPhone || '',
        email: supplier.email || '',
        seller_name: supplier.sellerName || '',
        seller_phone: supplier.sellerPhone || '',
        bank: supplier.bank?.id || '',
        bank_account: supplier.bankAccount || '',
        credit_days: supplier.creditDays !== undefined ? String(supplier.creditDays) : '',
        anticipation_days: supplier.anticipationDays !== undefined ? String(supplier.anticipationDays) : '',
        delivery_days: supplier.deliveryDays ? supplier.deliveryDays.split(',') : [],
        makes_deliveries: Boolean(supplier.makesDeliveries),
        provider_type: supplier.providerType?.value || 3
      });
      setErrors({});
      setActiveStep(0);
    }
  }, [supplier, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const handleDeliveryDaysChange = (event) => {
    setFormData((current) => ({ ...current, delivery_days: event.target.value }));
    setErrors((current) => ({ ...current, delivery_days: undefined }));
  };

  const handleMakesDeliveriesChange = (event) => {
    const makesDeliveries = event.target.checked;
    setFormData((current) => ({
      ...current,
      makes_deliveries: makesDeliveries,
      delivery_days: makesDeliveries ? current.delivery_days : []
    }));
    if (!makesDeliveries) setErrors((current) => ({ ...current, delivery_days: undefined }));
  };

  const getStepErrors = (step) => {
    const nextErrors = {};
    if (step === 0) {
      SUPPLIER_DETAILS_FIELDS.forEach((field) => {
        if (!String(formData[field] || '').trim()) nextErrors[field] = t(`${field}_required`);
      });
    }
    if (step === 2 && formData.makes_deliveries && formData.delivery_days.length === 0) {
      nextErrors.delivery_days = t('delivery_days_required');
    }
    return nextErrors;
  };

  const validateStep = (step) => {
    const nextErrors = getStepErrors(step);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep((current) => current + 1);
  };

  const handleSave = () => {
    const supplierErrors = getStepErrors(0);
    const deliveryErrors = getStepErrors(2);
    setErrors({ ...supplierErrors, ...deliveryErrors });

    const isSupplierValid = Object.keys(supplierErrors).length === 0;
    const isDeliveryValid = Object.keys(deliveryErrors).length === 0;
    if (!isSupplierValid || !isDeliveryValid) {
      setActiveStep(isSupplierValid ? 2 : 0);
      return;
    }

    const noBankId = bankOptions.find((bank) => bank.label === 'No aplica')?.id ?? 10;
    onSave({
      ...formData,
      bank: formData.bank || noBankId,
      delivery_days: formData.makes_deliveries ? formData.delivery_days.join(',') : '',
      credit_days: toNumberOrZero(formData.credit_days),
      anticipation_days: toNumberOrZero(formData.anticipation_days),
      provider_type: Number.parseInt(formData.provider_type, 10)
    });
    onClose();
  };

  if (!formData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle><span className="roboto-regular">{t('edit_supplier')}</span></DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ py: 2 }}>
          {steps.map((step) => <Step key={step}><StepLabel>{step}</StepLabel></Step>)}
        </Stepper>
        <SupplierFormSteps
          activeStep={activeStep}
          formData={formData}
          errors={errors}
          onChange={handleChange}
          onDeliveryDaysChange={handleDeliveryDaysChange}
          onMakesDeliveriesChange={handleMakesDeliveriesChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        {activeStep > 0 && <Button onClick={() => setActiveStep((current) => current - 1)}>{t('back')}</Button>}
        {activeStep < steps.length - 1 ? (
          <Button onClick={handleNext} variant="contained">{t('next')}</Button>
        ) : (
          <Button onClick={handleSave} variant="contained" color="primary">{t('save')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditSupplierModal;
